import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { EmployeeService } from '../../../employee/services/employee.service';
import { Employee, Shift } from '../../../employee/models/employee.models';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import {
  getCurrentMonthYear as getMonthYear,
  navigateMonth as navMonth,
  getCalendarDays as getDays,
  isToday as checkToday,
  isDayInSelectedWeek as checkDayInSelectedWeek,
  getWeekStart,
} from '../../utils/calendar.util';

export interface ScheduleShiftData {
  employees: string[];
  shift: string;
  week: string;
}

/**
 * Modal component for selecting employees, a shift, and a week range to schedule shifts for one or more employees.
 * It manages loading available employees and shifts, week navigation/calendar selection, form validation, and submitting schedule requests.
 * @component
 * @selector app-schedule-shift-modal
 * @example
 *   <app-schedule-shift-modal [isOpen]="true" (closeModal)="onModalClose()" (saveSchedule)="onScheduleSaved($event)"></app-schedule-shift-modal>
 * @input {boolean} isOpen - Controls whether the modal is open. When set to true the component resets its form and loads the latest employees and shifts.
 * @output {EventEmitter<void>} closeModal - Emitted after the close animation completes or when the modal is programmatically closed. Parent should listen to this to hide the modal.
 * @output {EventEmitter<ScheduleShiftData>} saveSchedule - Emitted after a successful schedule API response. Emits the schedule payload (employees, shift and week).
 *
 * Key behavior and responsibilities:
 * - Loads available employees and shifts from EmployeeService when the modal opens (ngOnChanges reacts to isOpen).
 * - Maintains internal loading states (loadingEmployees, loadingShifts) and saving state (isSaving).
 * - Validates the form via a computed isFormValid getter (requires at least one employee and a selected shift).
 * - Prevents closing while a save operation is in progress.
 * - Formats week start/end dates as YYYY-MM-DD when submitting to the API to avoid timezone issues.
 * - Replaces employee UUIDs with human readable names in API error messages via formatErrorMessage.
 *
 * Important lifecycle hooks:
 * - ngOnInit: intentionally does not load data to avoid unnecessary API calls; data is loaded when isOpen becomes true.
 * - ngOnChanges: listens for isOpen changes; when opened it resets the form and triggers loadEmployees and loadShifts.
 * - ngOnDestroy: completes destroy$ subject to clean up subscriptions.
 *
 * Key public methods:
 * - reloadShifts(): public helper to refresh available shifts (can be called by parent when shifts change).
 * - onClose(): safely closes the modal (honors isSaving and waits for close animation before emitting closeModal).
 * - onSave(): validates, formats the week range, submits schedule to the API, handles success and error flows and emits saveSchedule on success.
 * - toggleEmployeesDropdown(), toggleShiftDropdown(), toggleWeekDropdown(): toggle dropdown visibility while closing others.
 * - selectEmployee(), toggleEmployeeSelection(), removeEmployee(), getEmployeeInitials(): helpers for employee selection and display.
 * - toggleSelectAll(), isAllSelected(), isEmployeeSelected(): multi-select helpers for employee lists.
 * - selectShift(), getSelectedEmployeesText(), getSelectedShiftText(): shift/selection display helpers.
 * - getFilteredEmployees(): filters employees by search term and active status.
 * - navigateWeek(direction), updateWeekRange(): week navigation and range formatting (updates selectedWeekStart and scheduleData.week).
 * - onDocumentClick(event): clicks outside modal or dropdowns will close UI elements and/or modal (unless saving).
 * - toggleWeekCalendar(), calculateCalendarPosition(), getCurrentMonthYear(), navigateMonth(), getCalendarDays(), isToday(), isDayInSelectedWeek(), selectWeek():
 *   full set of week calendar helpers for month navigation, day calculations and selecting a week for scheduling.
 *
 * Internal / private helpers:
 * - resetForm(): resets UI state, selected employees/shift/week and search terms.
 * - loadEmployees(): loads a paginated list of employees (limited) and sets availableEmployees.
 * - loadShifts(): loads available shifts and sets availableShifts.
 * - formatErrorMessage(message: string): replaces UUIDs with employee names in server error messages to produce user friendly messages.
 *
 * Notes and considerations:
 * - Uses a Subject destroy$ with takeUntil to manage observable lifecycles and avoid memory leaks.
 * - Calendar positioning attempts to open upwards when there isn't enough space below the input.
 * - The component relies on injected services: EmployeeService for data operations and NotificationService for user-facing messages.
 */
@Component({
  selector: 'app-schedule-shift-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './schedule-shift-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./schedule-shift-modal.component.scss'],
})
export class ScheduleShiftModalComponent implements OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() saveSchedule = new EventEmitter<ScheduleShiftData>();

  private destroy$ = new Subject<void>();

  scheduleData: ScheduleShiftData = {
    employees: [],
    shift: '',
    week: '',
  };

  isClosing = false;
  loadingEmployees = false;
  loadingShifts = false;
  isSaving = false;

  get isFormValid(): boolean {
    return this.scheduleData.employees.length > 0 && this.scheduleData.shift !== '';
  }

  // Live data from API
  availableEmployees: Employee[] = [];
  availableShifts: Shift[] = [];

  // Week navigation
  currentWeek = new Date();
  weekRange = '';

  // Dropdown states
  showEmployeesDropdown = false;
  showShiftDropdown = false;
  showWeekDropdown = false;
  showWeekCalendar = false;

  // Search terms
  employeeSearchTerm = '';

  // Calendar properties
  dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  selectedWeekStart: Date | null = null;
  calendarPosition = { top: '0px', left: '0px', width: 'auto', openUpwards: false };

  constructor(
    private employeeService: EmployeeService,
    private messageService: NotificationService
  ) {
    // Initialize with current week
    this.selectedWeekStart = new Date(this.currentWeek);
    const dayOfWeek = this.selectedWeekStart.getDay();
    this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() - dayOfWeek);
    this.currentWeek = new Date(this.selectedWeekStart);
    this.updateWeekRange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      // Reset form when modal opens
      this.resetForm();
      // Reload shifts and employees when modal opens to get latest data
      this.loadShifts();
      this.loadEmployees();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Reset the schedule form and related UI state to default values (clears form data, closes dropdowns, resets week to current week, clears search and saving state).
   * @example
   * this.resetForm()
   * undefined
   * @param {void} none - This method accepts no arguments.
   * @returns {void} No return value.
   **/
  private resetForm(): void {
    // Clear form data
    this.scheduleData = {
      employees: [],
      shift: '',
      week: '',
    };

    // Reset dropdown states
    this.showEmployeesDropdown = false;
    this.showShiftDropdown = false;
    this.showWeekDropdown = false;
    this.showWeekCalendar = false;

    // Reset search terms
    this.employeeSearchTerm = '';

    // Reset to current week
    this.currentWeek = new Date();
    this.selectedWeekStart = new Date(this.currentWeek);
    const dayOfWeek = this.selectedWeekStart.getDay();
    this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() - dayOfWeek);
    this.currentWeek = new Date(this.selectedWeekStart);
    this.updateWeekRange();

    // Reset saving state
    this.isSaving = false;
  }

  /**
   * Load available employees for the schedule shift modal and update component state.
   * @example
   * this.loadEmployees()
   * // void — updates this.availableEmployees and this.loadingEmployees asynchronously
   * @returns {void} Loads the first page of employees (limit 100) and updates component state; clears employees on error.
   */
  private loadEmployees(): void {
    this.loadingEmployees = true;
    // Use a reasonable limit (100) instead of 1000 to avoid loading too many employees
    // If more employees are needed, consider implementing pagination or search
    this.employeeService
      .loadEmployees(1, 100, '', { locations: [], status: null })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.availableEmployees = response.employees || [];
          this.loadingEmployees = false;
        },
        error: error => {
          console.error('Error loading employees:', error);
          this.availableEmployees = [];
          this.loadingEmployees = false;
        },
      });
  }

  /**
   * Load available shifts from the EmployeeService, update component state (availableShifts and loadingShifts), and handle errors by logging and resetting state.
   * @example
   * loadShifts()
   * undefined
   * @returns {{void}} Does not return a value; updates the component's state asynchronously when shifts are loaded or an error occurs.
   **/
  private loadShifts(): void {
    this.loadingShifts = true;
    this.employeeService
      .loadShifts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: shifts => {
          this.availableShifts = shifts || [];
          this.loadingShifts = false;
        },
        error: error => {
          console.error('Error loading shifts:', error);
          this.availableShifts = [];
          this.loadingShifts = false;
        },
      });
  }

  // Public method to reload shifts (called from parent when shifts are added/deleted)
  public reloadShifts(): void {
    this.loadShifts();
  }

  /**
   * Close the schedule shift modal, preventing close while a save is in progress and avoiding duplicate close actions; waits for the close animation (300ms) before emitting the close event and resetting the form.
   * @example
   * nClose()
   * undefined
   * @returns {void} Emits the close event after the animation and resets the form; does not return a value.
   */
  onClose(): void {
    // Prevent closing while saving
    if (this.isSaving) {
      return;
    }

    if (!this.isClosing) {
      this.isClosing = true;
      // Wait for animation to complete before emitting close
      setTimeout(() => {
        this.closeModal.emit();
        this.isClosing = false;
        // Reset form after close animation completes
        this.resetForm();
      }, 300);
    }
  }

  /**
   * Validate the form and schedule the selected employees for the chosen week by formatting week start/end dates, calling the scheduling API, and handling success/error responses (emits events, resets form, shows messages).
   * @example
   * nSave()
   * void
   * @returns {{void}} Triggers API call and UI side effects; does not return a value.
   **/
  onSave(): void {
    if (!this.isFormValid || this.isSaving) {
      return;
    }

    this.isSaving = true;

    // Convert week range to start and end dates
    const weekStart = this.selectedWeekStart || new Date();
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    // Format dates as YYYY-MM-DD in local timezone to avoid timezone conversion issues
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const scheduleData = {
      employee_ids: this.scheduleData.employees,
      shift_id: this.scheduleData.shift,
      week_start: formatDate(weekStart),
      week_end: formatDate(weekEnd),
    };

    console.log('Scheduling shift with date range:', {
      week_start: formatDate(weekStart),
      week_end: formatDate(weekEnd),
      displayed_range: this.weekRange,
    });

    this.employeeService
      .scheduleShift(scheduleData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isSaving = false;
          if (response.status) {
            // Emit success to parent component - let parent handle the toast message
            this.saveSchedule.emit(this.scheduleData);
            this.resetForm();
            this.closeModal.emit();
          } else {
            // Replace employee ID with name in the error message
            const errorMessage = this.formatErrorMessage(
              response.message || 'Failed to schedule shift'
            );
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage,
              life: 5000,
            });
          }
        },
        error: error => {
          this.isSaving = false;
          console.error('Error scheduling shift:', error);
          // Extract error message from API response
          const errorMessage =
            error?.error?.message ||
            error?.message ||
            'Failed to schedule shift. Please try again.';
          const formattedMessage = this.formatErrorMessage(errorMessage);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: formattedMessage,
            life: 5000,
          });
        },
      });
  }

  toggleEmployeesDropdown(): void {
    this.showEmployeesDropdown = !this.showEmployeesDropdown;
    this.showShiftDropdown = false;
    this.showWeekDropdown = false;
  }

  toggleShiftDropdown(): void {
    this.showShiftDropdown = !this.showShiftDropdown;
    this.showEmployeesDropdown = false;
    this.showWeekDropdown = false;
  }

  toggleWeekDropdown(): void {
    this.showWeekDropdown = !this.showWeekDropdown;
    this.showEmployeesDropdown = false;
    this.showShiftDropdown = false;
  }

  selectEmployee(employee: Employee): void {
    if (!this.scheduleData.employees.includes(employee.id)) {
      this.scheduleData.employees.push(employee.id);
    }
    this.showEmployeesDropdown = false;
  }

  toggleEmployeeSelection(employee: Employee): void {
    const index = this.scheduleData.employees.indexOf(employee.id);
    if (index > -1) {
      this.scheduleData.employees.splice(index, 1);
    } else {
      this.scheduleData.employees.push(employee.id);
    }
  }

  isEmployeeSelected(employeeId: string): boolean {
    return this.scheduleData.employees.includes(employeeId);
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.scheduleData.employees = [];
    } else {
      this.scheduleData.employees = this.getFilteredEmployees().map(emp => emp.id);
    }
  }

  isAllSelected(): boolean {
    const filteredEmployees = this.getFilteredEmployees();
    return (
      filteredEmployees.length > 0 &&
      filteredEmployees.every(emp => this.scheduleData.employees.includes(emp.id))
    );
  }

  getEmployeeInitials(employee: Employee): string {
    const fullName = `${employee.first_name} ${employee.last_name}`;
    const names = fullName.split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
  }

  /**
   * Replace employee UUIDs found in an error message with the corresponding employee full names.
   * @example
   * formatErrorMessage("Failed to assign shift to 123e4567-e89b-12d3-a456-426614174000")
   * "Failed to assign shift to John Doe"
   * @param {{string}} {{message}} - The error message that may contain employee UUIDs to be replaced.
   * @returns {{string}} The formatted error message with UUIDs replaced by employee full names.
   **/
  private formatErrorMessage(message: string): string {
    // Replace employee IDs with employee names in the error message
    // Pattern to match UUIDs in the message
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

    let formattedMessage = message;
    const matches = message.match(uuidPattern);

    if (matches) {
      matches.forEach(employeeId => {
        const employee = this.availableEmployees.find(emp => emp.id === employeeId);
        if (employee) {
          const employeeName = `${employee.first_name} ${employee.last_name}`;
          formattedMessage = formattedMessage.replace(employeeId, employeeName);
        }
      });
    }

    return formattedMessage;
  }

  removeEmployee(employeeId: string): void {
    this.scheduleData.employees = this.scheduleData.employees.filter(id => id !== employeeId);
  }

  selectShift(shift: Shift): void {
    this.scheduleData.shift = shift.id;
    this.showShiftDropdown = false;
  }

  getSelectedEmployeesText(): string {
    if (this.scheduleData.employees.length === 0) {
      return 'Select employees';
    }
    if (this.scheduleData.employees.length === this.availableEmployees.length) {
      return `Selected (${this.scheduleData.employees.length}/${this.availableEmployees.length})`;
    }
    return `Selected (${this.scheduleData.employees.length}/${this.availableEmployees.length})`;
  }

  getSelectedShiftText(): string {
    if (!this.scheduleData.shift) {
      return 'Select shift';
    }
    const shift = this.availableShifts.find(s => s.id === this.scheduleData.shift);
    return shift ? shift.shift_name : 'Select shift';
  }

  getFilteredEmployees(): Employee[] {
    return this.availableEmployees.filter(employee => {
      // Only show active employees
      if (!employee.emp_status) {
        return false;
      }

      const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
      return fullName.includes(this.employeeSearchTerm.toLowerCase());
    });
  }

  navigateWeek(direction: 'prev' | 'next'): void {
    const daysToAdd = direction === 'next' ? 7 : -7;
    this.currentWeek = new Date(this.currentWeek.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
    this.updateWeekRange();
  }

  /**
   * Update the component's week range and selectedWeekStart based on this.currentWeek (uses Sunday as the start of the week).
   * @example
   * updateWeekRange()
   * // returns void
   * @returns {void} Updates this.selectedWeekStart, this.weekRange (formatted like "Sun, Jan 25 2026 - Sat, Jan 31 2026"), and this.scheduleData.week.
   */
  private updateWeekRange(): void {
    const startOfWeek = new Date(this.currentWeek);
    const dayOfWeek = startOfWeek.getDay();
    // Use Sunday as the start of the week (consistent with selectWeek method)
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    // IMPORTANT: Update selectedWeekStart whenever the week changes
    // This ensures the correct date is sent to the API
    this.selectedWeekStart = new Date(startOfWeek);

    const formatDate = (date: Date): string => {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthsShort = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];

      // Format: "Sun, Jan 25 2026"
      return `${days[date.getDay()]}, ${monthsShort[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;
    };

    this.weekRange = `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
    this.scheduleData.week = this.weekRange;
  }

  /**
   * Handle document click events to close the modal and dropdowns when clicking outside, unless a save operation is in progress.
   * @example
   * nDocumentClick(event)
   * void
   * @param {{Event}} {{event}} - The click event from the document.
   * @returns {{void}} No return value.
   **/
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Prevent closing modal while saving
    if (this.isSaving) {
      return;
    }
    // Close modal if clicking outside the modal content
    if (!target.closest('.modal-content')) {
      this.onClose();
    }
    // Close dropdowns if clicking outside them
    if (
      !target.closest('.dropdown-container') &&
      !target.closest('.week-container') &&
      !target.closest('.week-calendar-dropdown')
    ) {
      this.showEmployeesDropdown = false;
      this.showShiftDropdown = false;
      this.showWeekDropdown = false;
      this.showWeekCalendar = false;
    }
  }

  // Week Calendar Methods
  /**
   * Toggle the week calendar visibility, hide related dropdowns, and recalculate position if shown.
   * @example
   * oggleWeekCalendar()
   * undefined
   * @returns {void} No value is returned.
   */
  toggleWeekCalendar(): void {
    this.showWeekCalendar = !this.showWeekCalendar;
    this.showEmployeesDropdown = false;
    this.showShiftDropdown = false;

    if (this.showWeekCalendar) {
      // If a week is already selected, navigate to that month
      if (this.selectedWeekStart) {
        this.currentWeek = new Date(this.selectedWeekStart);
      }
      this.calculateCalendarPosition();
    }
  }

  /**
   * Calculate and set the absolute position and width of the calendar popup relative to the .week-input element, choosing to open upwards when there is insufficient space below.
   * @example
   * this.calculateCalendarPosition()
   * undefined
   * @returns {void} Updates this.calendarPosition with top, left, width (px strings) and openUpwards flag.
   **/
  private calculateCalendarPosition(): void {
    // Use setTimeout to ensure the DOM is updated
    setTimeout(() => {
      const weekInput = document.querySelector('.week-input') as HTMLElement;
      if (weekInput) {
        const rect = weekInput.getBoundingClientRect();
        const { left: rectLeft, width } = rect;
        const left = rectLeft + window.scrollX;

        // Estimate calendar height (header + 6 weeks of days + padding)
        const estimatedCalendarHeight = 280; // Approximate height
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top: number;
        let openUpwards = false;

        // Check if there's enough space below, if not, open upwards
        if (spaceBelow >= estimatedCalendarHeight + 20) {
          // Open downwards
          top = rect.bottom + window.scrollY + 8;
        } else if (spaceAbove >= estimatedCalendarHeight + 20) {
          // Open upwards
          top = rect.top + window.scrollY - estimatedCalendarHeight - 8;
          openUpwards = true;
        } else {
          // Default to downwards if neither has enough space
          top = rect.bottom + window.scrollY + 8;
        }

        this.calendarPosition = {
          top: `${top}px`,
          left: `${left}px`,
          width: `${width}px`,
          openUpwards: openUpwards,
        };
      }
    }, 0);
  }

  getCurrentMonthYear(): string {
    return getMonthYear(this.currentWeek);
  }

  navigateMonth(direction: 'prev' | 'next'): void {
    this.currentWeek = navMonth(this.currentWeek, direction);
  }

  getCalendarDays(): any[] {
    return getDays(this.currentWeek);
  }

  isToday(date: Date): boolean {
    return checkToday(date);
  }

  isDayInSelectedWeek(date: Date): boolean {
    return checkDayInSelectedWeek(date, this.selectedWeekStart);
  }

  selectWeek(selectedDate: Date): void {
    const weekStart = getWeekStart(selectedDate);
    this.selectedWeekStart = weekStart;
    this.currentWeek = weekStart;
    this.updateWeekRange();
    this.showWeekCalendar = false;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
